import { AccountStatusEnum, EnableDisableEnum, SnackBarVariants } from '@app/app.enum';
import { history } from '@app/app.routes';
import store from '@app/app.store';
import { withAxiosCancellation } from '@app/components/core/axios-cancel.component';
import withSnackBar from '@app/components/core/message.component';
import Icon from '@app/components/shared/icon.component';
import * as models from '@app/models';
import { IAccountAppResponseVM, IAccountAppViewProps } from '@app/models';
import Injector from '@app/services';
import { AccountService } from '@app/services/account.service';
import { IntlService } from '@app/services/intl.service';
import { SharedService } from '@app/services/shared.service';
import { SystemConfigService } from '@app/services/system-config.service';
import { Button, Grid } from '@material-ui/core';
import { StyleRules, withStyles } from '@material-ui/core/styles';
import Axios from 'axios';
import * as React from 'react';
import { injectIntl } from 'react-intl';
import AccountAppCardComponent from './account-app-card.component';

const systemConfigService: SystemConfigService = Injector.get(SystemConfigService);
const accountService: AccountService = Injector.get(AccountService);
const intlService: IntlService = Injector.get(IntlService);
const sharedService: SharedService = Injector.get(SharedService);

const styles: StyleRules = {
	card: {
		cursor: 'pointer',
	},
	cardHeading: {
		fontSize: 18,
	},
	cardWidth: {
		width: 300,
	},
	cardHeight: {
		height: 160,
	},
};

class AccountAppViewComponent extends React.Component<IAccountAppViewProps, any> {
	private isClosed: boolean;
	private userData: models.IUserSignInResponse;
	private apps: models.IAccountAppResponseVM[] = [];

	constructor(props: IAccountAppViewProps) {
		super(props);

		this.state = {
			addAppModalOpen: false,
			myApps: [],
			newAppDescription: null,
			newAppName: null,
			systemConfigAPIVersion: {},
			showDisabledApp: true,
		};

		this.userData = accountService.getUserData();
		this.handleClick = this.handleClick.bind(this);
		this.setIsClosed();
	}

	public appsFilterButton = (showVisibilityOffIcon: boolean) => (
		<Button
			startIcon={
				showVisibilityOffIcon ? (
					<Icon name='eye-slash' style={{ transform: 'translateY(-1.2px)' }} />
				) : (
					<Icon name='eye' style={{ transform: 'translateY(-2.2px)' }} />
				)
			}
			variant='text'
			classes={{ root: 'text-transform-none' }}
			onClick={this.applyDisabledAppFilter}
		>
			<strong>
				{intlService.GetFormattedMessage(
					showVisibilityOffIcon ? 'app.app-card.hide-disabled-apps-label' : 'app.app-card.show-disabled-apps-label'
				)}
			</strong>
		</Button>
	);

	public handleClick(e: React.MouseEvent, appState: models.IAccountAppResponseVM) {
		e.preventDefault();

		if (!accountService.IsAdmin(this.userData.user.role) && !this.props.isPartnerAccount) {
			this.props.history.push(`${models.Module.Apps.url}/app-detail`, {
				app: appState,
				breadcrumb: {
					title: appState.appName,
					location: history.location,
				},
			});
			return;
		}

		const breadCrumbArray = Array.isArray(this.props.history.location.state.breadcrumb)
			? this.props.history.location.state.breadcrumb
			: [{ ...this.props.history.location.state.breadcrumb }];
		breadCrumbArray.push({ title: appState.appName, location: this.props.history.location });
		const route = this.props.isPartnerAccount ? models.Module.Customers.url : models.Module.Accounts.url;
		this.props.history.push(`${route}/app/${appState.id}`, {
			app: appState,
			breadcrumb: breadCrumbArray,
		});
	}

	public componentDidMount() {
		if (accountService.IsAdmin(this.userData.user.role) || this.props.isPartnerAccount) {
			this.apps = this.props.apps;
			this.setState({
				myApps: this.props.apps,
			});
		} else {
			this.apps = this.userData.apps;
			this.setState({
				myApps: this.userData.apps,
			});
		}

		this.getApiVersion();
		this.applyDisabledAppFilter();
	}

	public render() {
		this.setIsClosed();
		const appsToShow = this.state.myApps as models.IAccountAppResponseVM[];
		const appNewCardCheck = !!this.apps.filter((x) => x.status === EnableDisableEnum.Enabled).length;

		let addAppCard = null;
		if (!this.isClosed && !this.props.isPartnerAccount) {
			addAppCard = (
				<>
					<Grid item={true} xs={appNewCardCheck ? 11 : 'auto'}>
						<AccountAppCardComponent
							classes={this.props.classes}
							onNewAppAdd={(app: models.IAccountAppResponseVM) => this.updateStateOfApps(app)}
							apiVersion={this.state.systemConfigAPIVersion}
							showNewAppCardAsButton={appNewCardCheck}
							appFilterButton={this.appsFilterButton(this.state.showDisabledApp)}
						/>
					</Grid>
				</>
			);
		}
		return (
			<React.Fragment>
				<Grid container={true} spacing={2}>
					<>
						{(!appNewCardCheck || this.isClosed || this.props.isPartnerAccount) && (
							<Grid item={true} xs={11}>
								<Grid container={true} justifyContent='flex-end'>
									<Grid item={true}>{this.appsFilterButton(this.state.showDisabledApp)}</Grid>
								</Grid>
							</Grid>
						)}
						{addAppCard}
					</>
					{appsToShow.map((a: models.IAccountAppResponseVM) => {
						return (
							<Grid item={true} key={a.id}>
								<AccountAppCardComponent
									key={a.id}
									classes={this.props.classes}
									app={a}
									totalNumberOfApps={appsToShow.length}
									handleClick={this.handleClick}
									onAppUpdate={this.onAppUpdate}
									onAppDeletion={this.onAppDeletion}
									onAppStatusUpdate={this.onAppStatusUpdate}
									apiVersion={this.state.systemConfigAPIVersion}
								/>
							</Grid>
						);
					})}
				</Grid>
			</React.Fragment>
		);
	}

	public onAppUpdate = (appData: IAccountAppResponseVM) => {
		// Get index of the app updated
		const currentAppIndex = this.apps.findIndex((x: models.IAccountAppResponseVM) => x.appId === appData.appId);

		// Update the app with new data
		this.apps[currentAppIndex] = appData;

		// Update state based on current filter applied
		const status = this.state.showDisabledApp
			? [EnableDisableEnum.Disabled, EnableDisableEnum.Enabled]
			: [EnableDisableEnum.Enabled];

		const myApps = this.getFilteredApps(status);

		this.setState({ myApps });

		// Feedback after app is updated
		this.props.openSnackBar(
			this.props.intl.formatMessage({
				id: 'app.snackbar.app-updated-successfully',
			})
		);
	};

	public onAppDeletion = (appId: string) => {
		const myApps = this.state.myApps.filter((x: models.IAccountAppResponseVM) => x.appId !== appId);

		this.apps = this.apps.filter((x) => x.appId !== appId);

		// Update local storage

		// start region
		if (!accountService.IsAdmin(this.userData.user.role)) {
			this.userData.apps = myApps;
			accountService.setUserData(this.userData);
		}
		// end region

		this.props.openSnackBar(intlService.GetFormattedMessage('app.snackbar.app-deleted-successfully'));
		this.setState({
			myApps,
		});
	};

	public onAppStatusUpdate = (appId: string, status: EnableDisableEnum) => {
		const currentAppIndex = this.apps.findIndex((x: models.IAccountAppResponseVM) => x.appId === appId);

		this.apps[currentAppIndex].status = status;

		// Sort app list
		this.apps.sort(this.sortEnabledApp).sort(this.sortDisabledApp);

		// Update local storage
		// start region
		if (!accountService.IsAdmin(this.userData.user.role)) {
			this.userData.apps = this.apps;
			accountService.setUserData(this.userData);
		}
		// end region

		const statusToCheck = this.state.showDisabledApp
			? [EnableDisableEnum.Enabled, EnableDisableEnum.Disabled]
			: [EnableDisableEnum.Enabled];

		this.setState({
			myApps: this.apps.filter((x) => statusToCheck.includes(x.status)),
		});

		this.props.openSnackBar(intlService.GetFormattedMessage(`app.snackbar.app-${status.toLowerCase()}-successfully`));
	};

	private updateStateOfApps = (app: models.IAccountAppResponseVM) => {
		if (!accountService.IsAdmin(this.userData.user.role)) {
			const userData = accountService.getUserData();
			userData.apps.unshift(app);
			accountService.setUserData(userData);
		}

		// Updating apps local property
		this.apps.unshift(app);

		this.setState((prevState: any) => ({
			myApps: [app, ...prevState.myApps],
		}));
	};

	private getApiVersion = async () => {
		try {
			const result = await systemConfigService.getAPIVersion(this.props.axiosCancellationToken);
			this.setState({
				systemConfigAPIVersion: result,
			});
		} catch (e) {
			if (!Axios.isCancel(e)) {
				this.props.openSnackBar(sharedService.parseError(e.data), SnackBarVariants.Error);
			}
		}
	};

	private setIsClosed = () => {
		this.isClosed =
			store.getState().authentication.status === AccountStatusEnum.Closed ||
			this.props?.accountStatus === AccountStatusEnum.Closed;
	};

	private getFilteredApps = (status: EnableDisableEnum[]) => {
		const filteredApps = ([...this.apps] as models.IAccountAppResponseVM[])
			.filter((x) => status.includes(x.status))
			.sort(this.sortEnabledApp)
			.sort(this.sortDisabledApp);

		return filteredApps;
	};

	private applyDisabledAppFilter = () => {
		// When a user clicks on 'Hide disabled apps' then at that moment showDisabledApp would be 'true'
		// So in order to filter only enabled apps when a user clicks on 'Hide disabled apps'
		// we are assigned Enabled status in the status array when this.state.showDisabledApp is true
		// and both status in the status array when this.state.showDisabledApp is false
		const status = this.state.showDisabledApp
			? [EnableDisableEnum.Enabled]
			: [EnableDisableEnum.Disabled, EnableDisableEnum.Enabled];

		this.userData = accountService.getUserData();

		const myApps = this.getFilteredApps(status);

		this.setState((prevState: any) => ({
			myApps,
			showDisabledApp: !prevState.showDisabledApp,
		}));
	};

	// App sorting methods
	private sortEnabledApp = (app1: models.IAccountAppResponseVM, app2: models.IAccountAppResponseVM) =>
		new Date(app2.dateCreated).getTime() - new Date(app1.dateCreated).getTime();

	private sortDisabledApp = (app1: models.IAccountAppResponseVM, app2: models.IAccountAppResponseVM) => {
		if (app1.status === EnableDisableEnum.Disabled && app2.status === EnableDisableEnum.Enabled) {
			return 1;
		} else if (app1.status === EnableDisableEnum.Enabled && app2.status === EnableDisableEnum.Disabled) {
			return -1;
		}
		return 0;
	};
}

export default withAxiosCancellation(withSnackBar(withStyles(styles)(injectIntl(AccountAppViewComponent))));



// WEBPACK FOOTER //
// ./src/components/account/app/account-app-view.component.tsx